import { useEffect } from 'react';
import { useSession } from 'next-auth/react';
import getConfig from 'next/config';
import { trackError } from 'lib/error.utils';
import logger from '@finance/shared/dist/utils/logger';
import { useIsDesktop } from 'lib/client/hooks/windowSizeHooks';

let canduLoadPromise: Promise<void> | null = null;

interface CanduTraits {
    email?: string;
    isMediumUp?: boolean;
    [key: string]: string | number | boolean | undefined;
}

interface CanduParams {
    clientToken: string;
    userId?: string;
    traits?: CanduTraits;
}

interface Candu {
    init(params: CanduParams): void;
    getEventing(): { identify: (userId: string, traits?: CanduTraits) => void };
    tearDown(): void;
    getMembership(): Promise<never>;
    refreshMembership(): void;
    renderContent(params: { slug: string; element?: HTMLElement; selector?: string }): void;
    triggerModal(params: { slug: string }): void;
    triggerTour(params: { slug: string }): void;
    triggerHotspotGroup(params: { slug: string }): void;
}

declare global {
    interface Window {
        Candu?: Candu;
    }
}

/**
 * Loads the Candu SDK script and initializes it with user data.
 * Can be called multiple times — subsequent calls re-init with updated traits
 * and refresh segment membership.
 */
function loadCanduScript({
    clientToken,
    userId,
    traits
}: {
    clientToken: string;
    userId: string;
    traits?: CanduTraits;
}): Promise<void> {
    // If Candu is already loaded, re-init with current traits and refresh segments
    if (window.Candu) {
        window.Candu.init({
            clientToken,
            userId,
            ...(traits && { traits })
        });
        window.Candu.refreshMembership();
        return Promise.resolve();
    }

    // Return existing promise if script is currently loading
    if (canduLoadPromise) {
        return canduLoadPromise;
    }

    canduLoadPromise = new Promise((resolve, reject) => {
        try {
            const script = document.createElement('script');
            script.setAttribute(
                'src',
                `https://cdn.candu.ai/sdk/latest/candu.umd.js?token=${clientToken}`
            );
            script.setAttribute('data-candu-script', 'true');
            script.async = true;
            script.defer = true;

            script.onload = function () {
                if (window.Candu) {
                    window.Candu.init({
                        clientToken,
                        userId,
                        ...(traits && { traits })
                    });
                }
                resolve();
            };

            script.onerror = function () {
                const error = new Error('Failed to load Candu script');
                canduLoadPromise = null;
                trackError({ error, context: { component: 'CanduProvider' } });
                reject(error);
            };

            document.head.appendChild(script);
        } catch (error) {
            canduLoadPromise = null;
            trackError({
                error: error instanceof Error ? error : new Error(String(error)),
                context: { component: 'CanduProvider' }
            });
            reject(error);
        }
    });

    return canduLoadPromise;
}

const CanduProvider = () => {
    const {
        publicRuntimeConfig: { env, canduClientToken }
    } = getConfig();
    const session = useSession();
    const userId = session.data?.user?._id;
    const email = session.data?.user?.email;
    const isMediumUp = useIsDesktop();

    useEffect(() => {
        if (env !== 'production') {
            return;
        }

        if (!userId || !canduClientToken) {
            return;
        }

        let cancelled = false;

        loadCanduScript({
            clientToken: canduClientToken,
            userId,
            traits: {
                ...(email && { email }),
                isMediumUp
            }
        }).catch(error => {
            if (!cancelled) {
                logger.error({ error, userId }, 'CanduProvider: failed to load Candu script');
            }
        });

        return () => {
            cancelled = true;
        };
    }, [env, canduClientToken, userId, email, isMediumUp]);

    return null;
};

export default CanduProvider;
